import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Send, Eye, Loader2, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const cents = (n) => `$${((n || 0) / 100).toFixed(2)}`;
const safeParse = (s) => { try { return parseISO(s); } catch { return null; } };

/**
 * "Create Invoice" dialog.
 *
 * Props:
 *   booking          — booking dict to pre-fill from (may be partial — needs at least `id`)
 *   trigger          — optional custom trigger node; defaults to a "Create Invoice" button
 *   onCreated(invoice)
 *   onSent(invoice)  — fires after the user hits Send (booking should be refreshed)
 */
export default function InvoiceBuilderDialog({ booking, trigger, onCreated, onSent }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('edit'); // 'edit' | 'preview'
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [invoice, setInvoice] = useState(null);   // last saved draft from server
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState('');
  const [tax, setTax] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [note, setNote] = useState('');

  // Initialize line items from the booking once the dialog opens
  useEffect(() => {
    if (!open) { setStep('edit'); return; }
    const horseCount = booking?.horseCount || booking?.jobAnimalCount || 1;
    const total = booking?.totalCents || 0;
    const unit = horseCount > 0 ? Math.round(total / horseCount) : total;
    setItems([
      {
        _key: Math.random().toString(36).slice(2),
        description: booking?.serviceName || booking?.service?.name || 'Service',
        quantity: horseCount || 1,
        unitPriceCents: unit,
      },
    ]);
    setDiscount('');
    setTax('');
    setDueDate('');
    setNote('');
    setInvoice(null);
  }, [open, booking]);

  const subtotal = items.reduce((sum, it) =>
    sum + Math.round((parseFloat(it.quantity) || 0) * (parseInt(it.unitPriceCents) || 0)), 0);
  const discountCents = Math.round((parseFloat(discount) || 0) * 100);
  const taxCents = Math.round((parseFloat(tax) || 0) * 100);
  const total = Math.max(0, subtotal - discountCents + taxCents);

  const setItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () =>
    setItems((prev) => [...prev, { _key: Math.random().toString(36).slice(2), description: '', quantity: 1, unitPriceCents: 0 }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const saveDraft = async () => {
    if (!items.length || items.some((it) => !it.description)) {
      toast.error('Add at least one line item with a description'); return null;
    }
    setSaving(true);
    try {
      const payload = {
        bookingId: booking?.id,
        clientId: booking?.clientId,
        clientName: booking?.clientName,
        clientEmail: booking?.clientEmail,
        clientPhone: booking?.clientPhone,
        jobServiceDate: booking?.startDateTime,
        jobLocation: booking?.jobAddress || booking?.jobLocation,
        jobAnimalCount: booking?.horseCount,
        jobServiceName: booking?.serviceName,
        dueDate: dueDate || null,
        discountCents,
        taxCents,
        invoiceNote: note || null,
        lineItems: items.map((it) => ({
          description: it.description,
          quantity: parseFloat(it.quantity) || 0,
          unitPriceCents: parseInt(it.unitPriceCents) || 0,
        })),
      };
      const { data } = await axios.post(`${API_URL}/api/dashboard/invoices`, payload, { headers });
      setInvoice(data);
      onCreated?.(data);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save invoice');
      return null;
    } finally { setSaving(false); }
  };

  const goPreview = async () => {
    const created = await saveDraft();
    if (created) setStep('preview');
  };

  const sendIt = async () => {
    let inv = invoice;
    if (!inv) inv = await saveDraft();
    if (!inv) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/dashboard/invoices/${inv.id}/send`, {}, { headers });
      toast.success('Invoice created and ready for client.');
      onSent?.({ ...inv, ...data, status: 'SENT' });
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const logoUrl = invoice?.businessLogoPath
    ? `${API_URL}/api/files/logos/${invoice.businessLogoPath.replace(/^ranchworks\/logos\//, '')}`
    : null;

  const [downloading, setDownloading] = useState(false);
  const downloadPdf = async () => {
    if (!invoice?.id) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/invoices/${invoice.id}/pdf`, {
        headers, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${invoice.invoiceNumber || invoice.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid={`create-invoice-btn-${booking?.id || 'new'}`}>
            <FileText className="h-4 w-4 mr-1" /> Create Invoice
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-builder-dialog">
        <DialogHeader>
          <DialogTitle>{step === 'edit' ? 'Create Invoice' : 'Preview'}</DialogTitle>
          <DialogDescription>
            {step === 'edit'
              ? 'Pre-filled from this job — edit line items, add a discount or tax, then preview.'
              : `Invoice ${invoice?.invoiceNumber} — review and send to ${invoice?.clientName || 'the client'}.`}
          </DialogDescription>
        </DialogHeader>

        {step === 'edit' ? (
          <div className="space-y-3">
            <div className="rounded border p-3 bg-muted/40 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{booking?.clientName || 'Client'}</span>
                <span className="text-xs text-muted-foreground">
                  {booking?.startDateTime && format(safeParse(booking.startDateTime) || new Date(), 'MMM d, yyyy')}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {booking?.serviceName} · {booking?.horseCount || 0} animal{(booking?.horseCount || 0) === 1 ? '' : 's'}
                {booking?.jobAddress ? ` · ${booking.jobAddress}` : ''}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Line items</Label>
              {items.map((it, idx) => (
                <div key={it._key} className="grid grid-cols-12 gap-2" data-testid={`invoice-item-row-${idx}`}>
                  <Input
                    className="col-span-6"
                    placeholder="Description"
                    value={it.description}
                    onChange={(e) => setItem(idx, { description: e.target.value })}
                    data-testid={`invoice-item-description-${idx}`}
                  />
                  <Input
                    className="col-span-2"
                    type="number" min="0" step="0.5"
                    placeholder="Qty"
                    value={it.quantity}
                    onChange={(e) => setItem(idx, { quantity: e.target.value })}
                    data-testid={`invoice-item-quantity-${idx}`}
                  />
                  <Input
                    className="col-span-3"
                    type="number" min="0" step="0.01"
                    placeholder="Unit $"
                    value={it.unitPriceCents ? (it.unitPriceCents / 100).toString() : ''}
                    onChange={(e) => setItem(idx, { unitPriceCents: Math.round((parseFloat(e.target.value) || 0) * 100) })}
                    data-testid={`invoice-item-price-${idx}`}
                  />
                  <Button variant="ghost" size="icon" className="col-span-1"
                    onClick={() => removeItem(idx)} data-testid={`invoice-item-remove-${idx}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addItem} data-testid="add-invoice-item-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add line
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div><Label>Discount $</Label>
                <Input type="number" step="0.01" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="discount-input" /></div>
              <div><Label>Tax $</Label>
                <Input type="number" step="0.01" min="0" value={tax} onChange={(e) => setTax(e.target.value)} data-testid="tax-input" /></div>
              <div><Label>Due date</Label>
                <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="due-date-input" /></div>
            </div>

            <div>
              <Label>Note</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional — overrides your default note for this invoice" data-testid="invoice-note-input" />
            </div>

            <div className="rounded border p-2 text-right text-sm space-y-0.5 bg-muted/20" data-testid="invoice-totals">
              <p>Subtotal <span className="font-medium ml-2 tabular-nums">{cents(subtotal)}</span></p>
              {discountCents > 0 && <p>Discount <span className="text-red-700 ml-2 tabular-nums">-{cents(discountCents)}</span></p>}
              {taxCents > 0 && <p>Tax <span className="ml-2 tabular-nums">+{cents(taxCents)}</span></p>}
              <p className="font-bold text-lg">Total <span className="ml-2 tabular-nums" data-testid="invoice-total">{cents(total)}</span></p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={goPreview} disabled={saving} data-testid="preview-invoice-btn">
                {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                <Eye className="h-4 w-4 mr-1" /> Preview
              </Button>
            </DialogFooter>
          </div>
        ) : (
          /* PREVIEW */
          <div className="space-y-4" data-testid="invoice-preview">
            <div className="rounded border bg-white p-4 sm:p-6 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo" className="h-12 mb-2 object-contain" data-testid="preview-logo" />
                  ) : null}
                  <p className="font-bold text-lg">{invoice?.businessName || 'Your Business'}</p>
                  <p className="text-xs text-muted-foreground">{invoice?.providerName}</p>
                  {invoice?.businessAddress && <p className="text-xs">{invoice.businessAddress}</p>}
                  {invoice?.businessPhone && <p className="text-xs">{invoice.businessPhone}</p>}
                  {invoice?.businessEmail && <p className="text-xs">{invoice.businessEmail}</p>}
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Invoice</p>
                  <p className="font-mono font-bold" data-testid="preview-invoice-number">{invoice?.invoiceNumber}</p>
                  <p className="text-xs mt-1">Date: {invoice?.invoiceDate}</p>
                  {invoice?.dueDate && <p className="text-xs">Due: {invoice.dueDate}</p>}
                </div>
              </div>

              <div className="text-sm">
                <p className="text-xs uppercase text-muted-foreground">Bill to</p>
                <p className="font-medium">{invoice?.clientName}</p>
                <p className="text-xs text-muted-foreground">
                  {invoice?.jobServiceName} · {invoice?.jobAnimalCount} animal{invoice?.jobAnimalCount === 1 ? '' : 's'}
                  {invoice?.jobServiceDate ? ` · ${format(safeParse(invoice.jobServiceDate) || new Date(), 'MMM d, yyyy')}` : ''}
                </p>
                {invoice?.jobLocation && <p className="text-xs">{invoice.jobLocation}</p>}
              </div>

              <table className="w-full text-sm">
                <thead className="border-b text-left">
                  <tr><th className="py-1">Description</th><th className="text-right">Qty</th><th className="text-right">Unit</th><th className="text-right">Total</th></tr>
                </thead>
                <tbody>
                  {(invoice?.lineItems || []).map((li) => (
                    <tr key={li.id} className="border-b" data-testid={`preview-item-${li.id}`}>
                      <td className="py-1">{li.description}</td>
                      <td className="text-right">{li.quantity}</td>
                      <td className="text-right tabular-nums">{cents(li.unitPriceCents)}</td>
                      <td className="text-right tabular-nums">{cents(li.totalCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="text-right text-sm space-y-0.5">
                <p>Subtotal <span className="tabular-nums ml-2">{cents(invoice?.subtotalCents)}</span></p>
                {(invoice?.discountCents || 0) > 0 && <p>Discount <span className="text-red-700 tabular-nums ml-2">-{cents(invoice.discountCents)}</span></p>}
                {(invoice?.taxCents || 0) > 0 && <p>Tax <span className="tabular-nums ml-2">+{cents(invoice.taxCents)}</span></p>}
                <p className="font-bold text-lg">Total <span className="tabular-nums ml-2">{cents(invoice?.totalCents)}</span></p>
              </div>

              {invoice?.invoiceNote && (
                <p className="text-sm italic border-l-2 pl-2 text-muted-foreground">{invoice.invoiceNote}</p>
              )}

              {(invoice?.paymentOptions || []).filter((p) => p.enabled).length > 0 && (
                <div className="rounded bg-muted/40 p-3 space-y-2">
                  <p className="text-xs font-semibold uppercase">How to pay</p>
                  {invoice.paymentOptions.filter((p) => p.enabled).map((p) => (
                    <div key={p.id} className="text-sm" data-testid={`preview-payment-${p.id}`}>
                      <p className="font-medium">{p.label}</p>
                      {p.value && <p className="text-xs">{p.value}</p>}
                      {p.instructions && <p className="text-xs whitespace-pre-line">{p.instructions}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('edit')}>Back to edit</Button>
              <Button variant="outline" onClick={downloadPdf} disabled={downloading} data-testid="download-invoice-pdf-btn">
                {downloading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                Download PDF
              </Button>
              <Button onClick={sendIt} disabled={sending} data-testid="send-invoice-btn">
                {sending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                <Send className="h-4 w-4 mr-1" /> Send Invoice
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
